import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { HoFeesShareApi } from '@/services/ho-fees-share.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select, Input } from '@/components/ui/Input';
import { fmtMoney } from '@/pages/fees/lib/feeFormat';
import { useDataTable, DataTablePagination } from '@/components/data-table';

/** Org tier — "Head Office Fees". Per-school 30% Head-Office-share reconciliation (owed vs already recorded via Head Office Fees Share) — fixed 30%, not configurable, matching the real legacy source exactly. */
export function OrgHeadOfficeFeesOverviewPage() {
  const [sessionYear, setSessionYear] = useState('');

  const { data: sessionYears = [] } = useQuery({
    queryKey: ['ho-fees-share-session-years'],
    queryFn: HoFeesShareApi.sessionYearsForOrg,
  });
  const effectiveSessionYear = sessionYear || sessionYears[0] || '';

  const { data: overview, isFetching } = useQuery({
    queryKey: ['org-ho-fees-overview', effectiveSessionYear],
    queryFn: () => HoFeesShareApi.overviewForOrg(effectiveSessionYear),
    enabled: !!effectiveSessionYear,
  });

  // All rows arrive in one shot (bounded by org school count), so search +
  // pagination happen client-side rather than round-tripping to the server.
  const dt = useDataTable({ defaultLimit: 20 });
  const allRows = overview?.rows ?? [];
  const filteredRows = useMemo(
    () => (dt.params.search ? allRows.filter((r) => r.schoolName.toLowerCase().includes(dt.params.search!.toLowerCase())) : allRows),
    [allRows, dt.params.search],
  );
  const total = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(total / dt.limit));
  const page = Math.min(dt.page, totalPages);
  const pageRows = filteredRows.slice((page - 1) * dt.limit, page * dt.limit);

  return (
    <div>
      <PageHeader title="Head Office Fees" description="Each school's 30% Head Office share, what's already been recorded as paid, and the outstanding balance." />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select className="w-48" value={effectiveSessionYear} onChange={(e) => setSessionYear(e.target.value)}>
          {sessionYears.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Select>
        <Input className="w-64" placeholder="Search school…" value={dt.searchInput} onChange={(e) => dt.setSearchInput(e.target.value)} />
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="card p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Total Head Office Share (30%)</div>
          <div className="mt-1 text-xl font-semibold text-slate-900">{fmtMoney(overview?.totalHeadOfficeShare ?? 0)}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Total Payments Received</div>
          <div className="mt-1 text-xl font-semibold text-green-600">{fmtMoney(overview?.totalPaymentsReceived ?? 0)}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Total Balance</div>
          <div className="mt-1 text-xl font-semibold text-amber-600">{fmtMoney(overview?.totalBalance ?? 0)}</div>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-slate-600">No.</th>
              <th className="px-4 py-2 text-left font-medium text-slate-600">School</th>
              <th className="px-4 py-2 text-left font-medium text-slate-600">Total Fees Collected</th>
              <th className="px-4 py-2 text-left font-medium text-slate-600">Head Office Share (30%)</th>
              <th className="px-4 py-2 text-left font-medium text-slate-600">Payments Received</th>
              <th className="px-4 py-2 text-left font-medium text-slate-600">Balance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pageRows.map((r, i) => (
              <tr key={r.schoolId} className="hover:bg-slate-50">
                <td className="px-4 py-2 text-slate-400">{(page - 1) * dt.limit + i + 1}</td>
                <td className="px-4 py-2 font-medium text-slate-800">{r.schoolName}</td>
                <td className="px-4 py-2 text-slate-600">{fmtMoney(r.totalFees)}</td>
                <td className="px-4 py-2 text-slate-600">{fmtMoney(r.headOfficeShare)}</td>
                <td className="px-4 py-2 text-green-600">{fmtMoney(r.paymentsSent)}</td>
                <td className="px-4 py-2 text-amber-600">{fmtMoney(r.balance)}</td>
              </tr>
            ))}
            {!isFetching && pageRows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-400">
                  {total === 0 && allRows.length === 0 ? `No fee data for ${effectiveSessionYear || 'this session year'}.` : 'No schools match your search.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <DataTablePagination state={dt} result={{ items: pageRows, total, page, limit: dt.limit, totalPages }} />
      </div>
    </div>
  );
}
